import {
  SocialExportConnectionMediaLimitsDto,
  SocialExportConnectionMediaQualityTierDto,
  SocialExportConnectionModalityMediaLimitsDto,
} from '@mi-front/api-v2';

/** Outcome of evaluating connection media limits for a clip duration. */
export type MediaLimitsIndication =
  | { kind: 'ok' }
  | { kind: 'blocked'; maxDurationSec?: number }
  | { kind: 'needs_transcoding'; targetQuality: number }
  | { kind: 'platform_displays_lower'; maxDisplayQuality: number };

/**
 * Coerces generated `object | null` numeric fields to a finite number or null.
 */
export function coerceNullableNumber(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Picks the best-fit quality tier: highest targetQuality whose maxDurationSec fits.
 * When durationSec is unknown, returns the highest tier (station tier) so soft tips
 * can still surface; block/downscale require a known duration and only apply when
 * a lower tier is selected.
 */
export function selectBestFitQualityTier(
  qualityTiers: SocialExportConnectionMediaQualityTierDto[],
  durationSec: number | undefined,
): SocialExportConnectionMediaQualityTierDto | undefined {
  const sorted = [...qualityTiers].sort((a, b) => b.targetQuality - a.targetQuality);

  if (durationSec == null) {
    return sorted[0];
  }

  return sorted.find((tier) => {
    const maxDurationSec = coerceNullableNumber(tier.maxDurationSec);
    return maxDurationSec == null || durationSec <= maxDurationSec;
  });
}

/**
 * Resolves the UX indication for publishing against platform media limits.
 * Branch on selected tier `delivery` only after best-fit selection.
 */
export function resolveMediaLimitsIndication(
  mediaLimits: SocialExportConnectionMediaLimitsDto | undefined,
  durationSec: number | undefined,
): MediaLimitsIndication {
  if (!mediaLimits) {
    return { kind: 'ok' };
  }

  const modality: SocialExportConnectionModalityMediaLimitsDto = mediaLimits.default;
  const selected = selectBestFitQualityTier(modality.qualityTiers, durationSec);

  if (!selected) {
    const modalityMax = coerceNullableNumber(modality.maxDurationSec);
    const tierMaxes = modality.qualityTiers
      .map((t) => coerceNullableNumber(t.maxDurationSec))
      .filter((n): n is number => n != null);
    const longestTierBudget = tierMaxes.length > 0 ? Math.max(...tierMaxes) : undefined;

    return {
      kind: 'blocked',
      maxDurationSec: modalityMax ?? longestTierBudget,
    };
  }

  switch (selected.delivery) {
    case 'as_source':
      return { kind: 'ok' };
    case 'platform_displays_lower': {
      const maxDisplayQuality = coerceNullableNumber(modality.maxDisplayQuality);
      if (maxDisplayQuality == null) {
        return { kind: 'ok' };
      }
      return { kind: 'platform_displays_lower', maxDisplayQuality };
    }
    case 'needs_transcoding':
      return { kind: 'needs_transcoding', targetQuality: selected.targetQuality };
    default:
      return { kind: 'ok' };
  }
}
